import { debounce, upperFirst } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { SpinnerSVGStyled } from '@src/components/Inputs/CheckboxInput/CheckboxInput.styles'
import { MerchantType } from '@src/graphql-types'
import { LocationType } from '@src/graphql-types'
import { useFindLocation } from '@src/hooks/useFindLocation'
import { useFulfilmentFilter } from '@src/hooks/useFulfilmentFilter/useFulfilmentFilter'
import { useMarketplace } from '@src/hooks/useMarketplace'

import {
  Container,
  Input,
  SearchButton,
  LocationButtonContainer,
  LocationButton,
  OuterContainer,
  SearchHeadContainer,
  StyledLocationSVG,
  TextHelp,
  ShowEverywhereText,
  HelpTextContainer,
  ResultsOuterContainer,
  ResultsOption,
  SpinnerContainer,
} from './HomePostcodeInput.styles'
import { useLazyQuery } from '@apollo/client'
import { SearchAddressDocument } from '@src/components/ServicePopover/queries/__generated__/SearchAddress.graphql-interface'
import { PlaceSearchLocation } from '@src/hooks/useFulfilmentFilter/validation'
import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'

interface SearchAddressPlace {
  placeId: string
  address: string
}
export const HomePostcodeInput: React.FC = () => {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const fulfilmentFilter = useFulfilmentFilter()
  const [postcode, setPostcode] = useState(
    fulfilmentFilter.data.where.historicalData.postAndCountryCode?.postcode ||
      ''
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [queriedTerm, setQueriedTerm] = useState('')
  const [newPlace, setNewPlace] = useState<Omit<
    PlaceSearchLocation,
    'type'
  > | null>(null)

  const navigate = useNavigate()
  const findLocation = useFindLocation({
    onSuccess: coordinates => {
      fulfilmentFilter.setLocationType({
        type: LocationType.COORDINATES,
        coordinates,
      })
      setFindingLocation(false)
      navigate(`/${merchantURLPath}`)
    },
    onError: () => {
      setFindingLocation(false)
    },
  })
  const [findingLocation, setFindingLocation] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const marketplace = useMarketplace()
  const merchantURLPath = marketplace.urlPath
  const { heroAlignment, allowLocationSearch, id } = marketplace

  const { postcode: postcodePlaceholder } = marketplace.contactAddress

  const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const upperCaseValue = value.toUpperCase()
    setPostcode(upperCaseValue)
  }

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    setIsSubmitting(true)

    if (allowLocationSearch) {
      if (!newPlace) return

      fulfilmentFilter.setLocationType({
        type: LocationType.PLACE_SEARCH,
        placeId: newPlace.placeId,
        address: newPlace.address,
      })
    } else {
      fulfilmentFilter.setLocationType({
        type: LocationType.POSTCODE,
        postAndCountryCode: {
          postcode,
          countryCode: marketplace.country.ISO3166Alpha2,
        },
      })
    }
    navigate(`/${merchantURLPath}`)
  }

  const handleShowEverywhere = (): void => {
    fulfilmentFilter.setLocationType({
      type: LocationType.EVERYWHERE,
    })
    navigate(`/${merchantURLPath}`)
  }

  const getSearchTerm = () => {
    // return merchantURL path if exists e.g. Florists
    if (merchantURLPath) return upperFirst(merchantURLPath)
    // else if restaurant, return 'Restaurants', if retail return 'Outlets'
    return marketplace.merchantType === MerchantType.RETAIL
      ? 'Outlets'
      : 'Restaurants'
  }

  const [
    triggerQuery,
    { data: _data, previousData, loading: searchAddressLoading },
  ] = useLazyQuery(SearchAddressDocument)

  const isSelectedPlaceActive = newPlace && searchTerm === newPlace.address

  const data =
    (searchTerm === queriedTerm && searchTerm.length > 0) ||
    isSelectedPlaceActive
      ? _data?.searchAddress || previousData?.searchAddress
      : undefined

  const debouncedSearch = useMemo(
    () =>
      debounce((term: string) => {
        if (term.length >= 5) {
          setQueriedTerm(term)
          void triggerQuery({
            variables: {
              searchTerm: term,
              marketplaceId: id,
            },
          })
        }
      }, 500),
    [id, triggerQuery]
  )

  useEffect(() => {
    if (newPlace && searchTerm === newPlace.address) {
      debouncedSearch.cancel()
      return
    }
    if (searchTerm.length >= 5) {
      debouncedSearch(searchTerm)
    } else {
      setQueriedTerm('')
    }
  }, [searchTerm, newPlace, debouncedSearch])

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  // Show options only if place hasn't been selected or user is typing
  const isEditingSelection = !newPlace || searchTerm !== newPlace.address
  const shouldShowOptions =
    isFocused &&
    (searchTerm === queriedTerm || isSelectedPlaceActive) &&
    !!data?.length
  const shouldShowLoading =
    isFocused &&
    isEditingSelection &&
    searchTerm.length >= 5 &&
    searchAddressLoading

  const handleRadioChange = (place: SearchAddressPlace) => {
    setNewPlace(place)
    setSearchTerm(place.address)
    setIsFocused(false)
  }

  return (
    <OuterContainer $alignment={heroAlignment}>
      <HelpTextContainer>
        <TextHelp>
          {t('search_near_me_help', {
            searchTerm: getSearchTerm().toLocaleLowerCase(),
            searchType: allowLocationSearch
              ? t('location_in_sentence')
              : t('postcode_in_sentence'),
          })}
          <ShowEverywhereText onClick={() => handleShowEverywhere()}>
            {t('show_everything')}
          </ShowEverywhereText>
          {'.'}
        </TextHelp>
      </HelpTextContainer>
      <SearchHeadContainer>
        <Container
          onSubmit={e => handleFormSubmit(e)}
          resultsOpen={shouldShowOptions || shouldShowLoading}
        >
          <LocationButtonContainer>
            <LocationButton
              id={t('use_current_location')}
              onClick={() => {
                setFindingLocation(true)
                findLocation()
              }}
              type="button"
            >
              {findingLocation ? (
                <SpinnerSVGStyled id="locationLoadSVG" height={32} width={32} />
              ) : (
                <StyledLocationSVG id="postcode" />
              )}
            </LocationButton>
            <Input
              name="postal-code"
              autoComplete="home postal-code"
              required
              type="text"
              data-1p-ignore
              placeholder={t('enter_your_postcode', {
                postcode: postcodePlaceholder ?? 'SW1 1AA',
              })}
              onChange={
                allowLocationSearch
                  ? handleLocationChange
                  : handlePostcodeChange
              }
              value={allowLocationSearch ? searchTerm : postcode}
              title={t('enter_your_postcode_alt', {
                postcode: postcodePlaceholder ?? 'SW1 1AA',
              })}
              onFocus={() => setIsFocused(true)}
              onClick={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
          </LocationButtonContainer>
          <SearchButton
            type="submit"
            disabled={
              allowLocationSearch
                ? searchTerm.length <= 5
                : postcode.length <= 2
            }
            content={t('search_near_me', { searchTerm: getSearchTerm() })}
            loading={isSubmitting}
          />
        </Container>
        {(shouldShowOptions || shouldShowLoading) && (
          <ResultsOuterContainer onMouseDown={e => e.preventDefault()}>
            {shouldShowLoading ? (
              <SpinnerContainer>
                <LoadingSpinner />
              </SpinnerContainer>
            ) : (
              data?.map?.(place => (
                <ResultsOption
                  key={place.placeId}
                  type="button"
                  onClick={() => handleRadioChange(place)}
                  aria-label={place.address}
                >
                  {place.address}
                </ResultsOption>
              ))
            )}
          </ResultsOuterContainer>
        )}
      </SearchHeadContainer>
    </OuterContainer>
  )
}
